import apiInstance from '@/axios';
import axios from 'axios';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/types';

const BASE_URL = process.env.VUE_APP_SERVERURL;

// ── 타입 정의 ──

export interface EmailInfo {
  emailId: string;
  email: string;
  verified: boolean;
  createdAt: string;
}

export interface MaskedEmailInfo {
  emailId: string;
  email: string;
}

export interface TwoFactorStatus {
  enabled: boolean;
  methods: TwoFactorMethod[];
  preferredMethod?: TwoFactorMethod;
  emails: EmailInfo[];
  passkeys: PasskeyInfo[];
}

export interface PasskeyInfo {
  credentialId: string;
  name: string;
  createdAt: string;
}

export type TwoFactorMethod = 'EMAIL_OTP' | 'PASSKEY';

export interface TwoFactorLoginResponse {
  twoFactorRequired: true;
  twoFactorToken: string;
  availableMethods: TwoFactorMethod[];
  emails: MaskedEmailInfo[];
  passwordChangeRequired: boolean;
}

export interface TwoFactorVerifyResponse {
  accessToken: string;
  refreshToken: string;
}

export interface ChallengeEmailResponse {
  method: 'EMAIL_OTP';
  message: string;
}

export interface ChallengePasskeyResponse {
  method: 'PASSKEY';
  options: PublicKeyCredentialRequestOptionsJSON;
}

export type ChallengeResponse =
  | ChallengeEmailResponse
  | ChallengePasskeyResponse;

export interface SetupEmailOtpResponse {
  message: string;
  emailId: string;
}

// ── 설정 API (accessToken 인증) ──

export function get2FAStatus() {
  return apiInstance.get<TwoFactorStatus>('/auth/2fa/status');
}

export function setupEmailOtp(email: string) {
  return apiInstance.post<SetupEmailOtpResponse>('/auth/2fa/setup/email-otp', {
    email,
  });
}

export function verifyEmailOtpSetup(code: string) {
  return apiInstance.post('/auth/2fa/setup/email-otp/verify', { code });
}

export function deleteEmailById(emailId: string) {
  return apiInstance.delete(`/auth/2fa/setup/email-otp/${emailId}`);
}

export function getPasskeyOptions() {
  return apiInstance.post<PublicKeyCredentialCreationOptionsJSON>(
    '/auth/2fa/setup/passkey/options'
  );
}

export function verifyPasskeySetup(
  response: RegistrationResponseJSON,
  name?: string
) {
  return apiInstance.post('/auth/2fa/setup/passkey/verify', {
    response,
    name,
  });
}

export function deletePasskey(credentialId: string) {
  return apiInstance.delete(`/auth/2fa/setup/passkey/${credentialId}`);
}

export function setPreferredMethod(method: TwoFactorMethod) {
  return apiInstance.put('/auth/2fa/preferred-method', { method });
}

// ── 로그인 2FA 검증 API (twoFactorToken 인증, accessToken 불필요) ──

export function request2FAChallenge(
  twoFactorToken: string,
  method: TwoFactorMethod,
  emailId?: string
) {
  const body: Record<string, unknown> = { twoFactorToken, method };
  if (method === 'EMAIL_OTP' && emailId) {
    body.emailId = emailId;
  }
  return axios.post<ChallengeResponse>(`${BASE_URL}/auth/2fa/challenge`, body);
}

export function verify2FAEmailOtp(twoFactorToken: string, code: string) {
  // 로그인 완료 응답이 refresh 쿠키를 심으므로 쿠키를 받는다(K-19)
  return axios.post<TwoFactorVerifyResponse>(
    `${BASE_URL}/auth/2fa/verify`,
    { twoFactorToken, method: 'EMAIL_OTP', code },
    { withCredentials: true }
  );
}

export function verify2FAPasskey(
  twoFactorToken: string,
  response: AuthenticationResponseJSON
) {
  return axios.post<TwoFactorVerifyResponse>(
    `${BASE_URL}/auth/2fa/verify`,
    { twoFactorToken, method: 'PASSKEY', response },
    { withCredentials: true }
  );
}
